import { apiClient, APIResponse, PaginatedResponse, QueryOptions } from './client';

export enum ScanType {
  FULL = 'full',
  INCREMENTAL = 'incremental',
  LICENSE_ONLY = 'license_only',
  VULNERABILITY_ONLY = 'vulnerability_only',
  ORT_LICENSE_SCAN = 'ort_license_scan',
  ORT_VULNERABILITY_SCAN = 'ort_vulnerability_scan',
  ORT_FULL_SCAN = 'ort_full_scan'
}

export enum ScanStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  CONFIGURED = 'configured'
}

export interface Scan {
  id: number;
  workgroupid: number;
  projectid: number;
  policyid?: number;
  scantype: ScanType;
  code: string;
  progresspercentage: number;
  scanparameters?: any;
  version?: string;
  filesscanned: number;
  componentsfound: number;
  projectsize?: number;
  vulnerabilitiesfound: number;
  licenseissuesfound: number;
  overallscore?: number;
  vulnerabilityscore?: number;
  licensescore?: number;
  operationalriskscore?: number;
  technicaldebtscore?: number;
  businessriskscore?: number;
  businesscriticality?: string;
  custompolicystandards?: any;
  startdate?: string;
  completedate?: string;
  errormessage?: string;
  scanlog?: string;
  scanstatus: ScanStatus;
  status: string;
  description?: string;
  isactive: boolean;
  isarchived: boolean;
  isdeleted: boolean;
  createdon: string;
  createdby: number;
  lastactivityon?: string;
  lastactivityby?: number;

  project_name?: string;
  repourl: string;
  scanrepourl?: string;
  has_github_authentication?: boolean;
  // Per-scan snapshot fields, stamped server-side at creation/trigger/processing time --
  // authoritative for this scan and immune to the parent project's repo being later
  // reconnected/edited. null on scans that predate this column; consumers should fall back to
  // scanparameters.scanMethod, then repourl truthiness, for those.
  scanorigin?: 'github' | 'manual';
  scanrepoowner?: string;
  scanreponame?: string;
  githubtokensource?: 'project' | 'service';
  resultformat?: 'ort' | 'cyclonedx' | 'spdx';
  applicationid?: number;
  application_name?: string;
  applicationtype?: string;
  application_business_criticality?: string;

  creator_firstname?: string;
  creator_lastname?: string;
  creator_emailid?: string;
  creator_designation?: string;
  creator_department?: string;

  total_components?: number;
  unique_components?: number;
  top_component_types?: string;
  encryptioncomponents?: number;
  provenancecomponents?: number;

  total_vulnerabilities?: number;
  critical_vulnerabilities?: number;
  high_vulnerabilities?: number;
  medium_vulnerabilities?: number;
  low_vulnerabilities?: number;
  unique_cves?: number;
  avg_cvss_score?: number;
  max_cvss_score?: number;
  exploitable_vulnerabilities?: number;

  curatedlocations?: [];
  declaredlocations?: [];

  total_license_issues?: number;
  critical_license_issues?: number;
  high_license_issues?: number;
  medium_license_issues?: number;
  low_license_issues?: number;
  policy_violations?: number;
  unique_licenses?: number;

  scan_duration_minutes?: number;
  scan_throughput_files_per_minute?: number;
  scan_quality_score?: number;
  encryption_count?: number;
  provenance_count?: number;
  risk_level?: string;

  projects?: any;
  components?: any[];
  vulnerabilities?: any[];
}

export interface CreateScanData {
  projectid: number;
  workgroupid: number;
  applicationid: number;
  policyid?: number;
  scantype?: ScanType;
  code?: string;
  version?: string;
  scanparameters?: any;
  custompolicystandards?: any;
  businesscriticality?: string;
  description?: string;
  status?: string;
}

export interface UpdateScanData {
  progresspercentage?: number;
  filesscanned?: number;
  componentsfound?: number;
  projectsize?: number;
  vulnerabilitiesfound?: number;
  licenseissuesfound?: number;
  overallscore?: number;
  vulnerabilityscore?: number;
  licensescore?: number;
  operationalriskscore?: number;
  technicaldebtscore?: number;
  businessriskscore?: number;
  businesscriticality?: string;
  custompolicystandards?: any;
  startdate?: Date;
  completedate?: Date;
  errormessage?: string;
  scanlog?: string;
  status?: string;
  isarchived?: boolean;
}

export interface FindScansOptions extends QueryOptions {
  projectId?: number;
  applicationId?: number;
}

export interface ScanComponent {
  id: number;
  scanid: number;
  applicationid: number;
  name: string;
  version?: string;
  componenttype?: string;
  language?: string;
  filepath?: string;
  packagemanager?: string;
  description?: string;
  homepageurl?: string;
  licensename?: string;
  licensemainid?: number;
  license_spdx_identifier?: string;
  licenseurl?: string;
  isdirectdependency: boolean;
  createdon: string;
  licensecategory?: string;
  vulnerabilities: any[];
  licenseissues: any[];
  isdeprecated?: boolean;
  isoutdated?: boolean;
  curatedlocations?: any[];

  referenceid?: string;
  isencrypted?: boolean;
  purl?: string;
  cryptographyalgorithms?: any[];
  binaryartifacturl?: string;
  sourceartifacturl?: string;
  dayssincelastcommit?: number;
  declaredlocations?: any[];
  total_vulnerabilities?: number;
  critical_vulnerabilities?: number;
  high_vulnerabilities?: number;
  medium_vulnerabilities?: number;
  low_vulnerabilities?: number;
  unique_cves?: number;
  avg_cvss_score?: number;
  max_cvss_score?: number;
  min_cvss_score?: number;
  exploitable_vulnerabilities?: number;
  avg_epss_score?: number;
  max_epss_score?: number;
  min_epss_score?: number;
  highest_epss_severity?: string;
  high_epss_vulnerabilities?: number;
  medium_epss_vulnerabilities?: number;
  low_epss_vulnerabilities?: number;
  exploit_db_available_count?: number;
  fix_available_count?: number;
  total_license_issues?: number;
  critical_license_issues?: number;
  high_license_issues?: number;
  medium_license_issues?: number;
  low_license_issues?: number;
  policy_violations?: number;
  unique_license_types?: number;
  unique_issue_types?: number;
  license_issues_with_recommendations?: number;
  primary_license_issue_type?: string;
  license_risk_level?: string;
  risk_level?: string;
  risk_score?: number;
  encryption_status?: string;
  deprecation_status?: string;
  currency_status?: string;
  maintenance_status?: string;
  has_crypto_algorithms?: boolean;
  crypto_algorithm_count?: number;
  has_declared_locations?: boolean;
  has_curated_locations?: boolean;
  compliance_score?: number;
}

export interface ScanVulnerability {
  id: number;
  title?: string;
  componentid: number;
  cveid?: string;
  severity: string;
  cvssscore?: number;
  epssscore?: number;
  epssseverity: string;
  description?: string;
  recommendation?: string;
  publisheddate?: string;
  lastmodifieddate?: string;
  createdon: string;
  components: ScanComponent;
  risklevel: string;
  fixavailable: boolean;
  exploitavailable: boolean;
}

export interface ScanLicenseIssue {
  id: number;
  scanid: number;
  componentid: number;
  licensename?: string;
  issuetype?: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  description?: string;
  recommendation?: string;
  policyviolation: boolean;
  createdon: string;
  components: ScanComponent;
}

export interface ScanTrendData {
  scanId: number;
  date: string;
  overallScore: number;
  vulnerabilityScore: number;
  licenseScore: number;
  componentCount: number;
  vulnerabilityCount: number;
}

export interface SurveyQuestionOption {
  value: string;
  label: string;
  score: number;
}

export interface SurveyQuestion {
  type: string;
  question: string;
  options: SurveyQuestionOption[];
}

export interface OrtScanConfiguration {
  repourl: string;
  repourlmain?: string;
  repoversion?: string;
  branchname: string;
  repoaccesstoken: string;
  repousername?: string;
  repoprovider?: string;
  ort_config?: {
    scan_type: string;
    licenseRules?: Record<string, string>;
  };
  webhook_url?: string;
}

export class ScanAPI {
  static async getAll(options?: FindScansOptions): Promise<APIResponse<PaginatedResponse<Scan>>> {
    return apiClient.get<PaginatedResponse<Scan>>('/scans', options);
  }

  static async getById(id: number): Promise<APIResponse<Scan>> {
    return apiClient.get<Scan>(`/scans/${id}`);
  }

  static async create(data: CreateScanData): Promise<APIResponse<Scan>> {
    return apiClient.post<Scan>('/scans', data);
  }

  static async update(id: number, data: UpdateScanData): Promise<APIResponse<Scan>> {
    return apiClient.patch<Scan>(`/scans/${id}`, data);
  }


  static async delete(id: number): Promise<APIResponse<void>> {
    return apiClient.delete<void>(`/scans/${id}`);
  }

  static async archive(id: number): Promise<APIResponse<void>> {
    return apiClient.delete<void>(`/scans/archive/${id}`);
  }

  static async restore(id: number): Promise<APIResponse<void>> {
    return apiClient.patch<void>(`/scans/restore/${id}`);
  }

  static async getResults(scanId: number): Promise<APIResponse<any[]>> {
    return apiClient.get<any[]>(`/scans/results/${scanId}`);
  }

  static async getComponents(scanId: number): Promise<APIResponse<ScanComponent[]>> {
    return apiClient.get<ScanComponent[]>(`/scans/components/${scanId}`);
  }

  static async getVulnerabilities(scanId: number): Promise<APIResponse<ScanVulnerability[]>> {
    return apiClient.get<ScanVulnerability[]>(`/scans/vulnerabilities/${scanId}`);
  }

  static async getLicenseIssues(scanId: number): Promise<APIResponse<ScanLicenseIssue[]>> {
    return apiClient.get<ScanLicenseIssue[]>(`/scans/license-issues/${scanId}`);
  }

  static async startOrtScan(config: OrtScanConfiguration): Promise<APIResponse<{ runId: number; message: string; status: string }>> {
    return apiClient.post<{ runId: number; message: string; status: string }>('/scans/ort/start', config);
  }

  static async getOrtScanStatus(scanId: number): Promise<APIResponse<{
    scanId: number;
    status: string;
    progress: number;
    startedAt?: string;
    completedAt?: string;
    errorMessage?: string;
    results?: any[];
  }>> {
    return apiClient.get(`/scans/ort/status/${scanId}`);
  }


  static async cancelOrtScan(id: number): Promise<APIResponse<Scan>> {
    return apiClient.patch<Scan>(`/scans/ort/cancel/${id}`);
  }

  static async downloadSccReport(scanId: number): Promise<void> {
    try {
      const arrayBuffer = await apiClient.download(`/scans/${scanId}/download-scc-report`);

      const blob = new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const scanResponse = await this.getById(scanId);
      const scan = scanResponse.data;

      if (!scan) {
        throw new Error('Scan not found');
      }
      const projectName = scan.project_name || 'project';
      const version = scan.version || 'unknown';

      const sanitizedProjectName = projectName.replace(/[<>:"/\\|?*\s]/g, '_').toLowerCase();
      const sanitizedVersion = version.replace(/[<>:"/\\|?*\s]/g, '_').toLowerCase();
      const fileName = `${sanitizedProjectName}_${sanitizedVersion}_report.xlsx`;


      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      throw new Error(error.message || 'Failed to download SCC report');
    }
  }


  static async downloadOrtReport(scanId: number): Promise<void> {
    try {
      const arrayBuffer = await apiClient.download(`/scans/${scanId}/download-report`);

      const blob = new Blob([arrayBuffer], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);


      const scanResponse = await this.getById(scanId);
      const scan = scanResponse.data;

      if (!scan) {
        throw new Error('Scan not found');
      }
      const projectName = scan.project_name || 'project';
      const version = scan.version || 'unknown';

      const sanitizedProjectName = projectName.replace(/[<>:"/\\|?*\s]/g, '_').toLowerCase();
      const sanitizedVersion = version.replace(/[<>:"/\\|?*\s]/g, '_').toLowerCase();
      const fileName = `${sanitizedProjectName}_${sanitizedVersion}_ort-files.zip`;

      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      throw new Error(error.message || 'Failed to download ORT report');
    }
  }

  static async uploadOrtFiles(scanId: number, file: File): Promise<APIResponse<{ message: string }>> {
    const formData = new FormData();
    formData.append('ortFile', file);

    return apiClient.upload<{ message: string }>(`/scans/${scanId}/upload-ort`, formData);
  }

  static async cancel(scanId: number): Promise<APIResponse<Scan>> {
    return apiClient.patch<Scan>(`/scans/${scanId}/cancel`);
  }

  static async getLatestSuccessfulScanByProject(projectId: number): Promise<APIResponse<Scan>> {
    return apiClient.get<Scan>(`/scans/project/${projectId}/latest-successful`);
  }

  static async validateVersion(projectId: number, version: string, excludeScanId?: number): Promise<APIResponse<{ isValid: boolean; message?: string }>> {
    const params = new URLSearchParams({
      version,
      ...(excludeScanId && { excludeScanId: excludeScanId.toString() })
    });
    return apiClient.get<{ isValid: boolean; message?: string }>(`/scans/project/${projectId}/validate-version?${params}`);
  }

  static async getSurveyQuestions(): Promise<APIResponse<SurveyQuestion[]>> {
    return apiClient.get<SurveyQuestion[]>('/scans/survey-questions');
  }

}
